// ManagerEntitiesModal.tsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  Typography,
  Divider,
  Tooltip,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FolderPermissionResponse,
  getAllUsersAndFoldersPermissions,
  removePermissionsForUsers,
} from "../api";
import EntityFilter from "./EntityFilter";
import * as XLSX from "xlsx";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";

interface FolderPermission {
  id: string; // Alterado de number para string
  entidade: string | null;
  login: string;
  nome: string;
  pasta: string;
}

interface UserFoldersPermissionsResponse {
  userId: number;
  fullName: string;
  login: string;
  email: string;
  permissions: FolderPermissionResponse[];
}

interface UserToDelete {
  userId: string;
  login: string;
  name: string;
  entidades: string[];
}

interface ManagerEntitiesModalProps {
  open: boolean;
  onClose: () => void;
}

const ManagerEntitiesModal: React.FC<ManagerEntitiesModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();

  // Estado para gerenciar seleções
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Estados para filtros
  const [filterType, setFilterType] = useState<"login" | "nome">("login");
  const [filterValue, setFilterValue] = useState<string>("");
  const [filterEntidades, setFilterEntidades] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<FolderPermission[]>([]);
  const [loading, setLoading] = useState<boolean>(false); // Estado de carregamento
  const [error, setError] = useState<string>(""); // Estado de erro
  const storedUser = sessionStorage.getItem("user");
  const currentUser = storedUser ? JSON.parse(storedUser).login : "";
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] =
    useState<boolean>(false);
  const [foldersToRemove, setFoldersToRemove] = useState<FolderPermission[]>(
    []
  );
  const [usersToDelete, setUsersToDelete] = useState<UserToDelete[]>([]);
  const [shouldShowContent, setShouldShowContent] = useState(false);

  const getHighestLevelFolders = (permissions: FolderPermissionResponse[]) => {
    const folderPaths = permissions.map((p) => normalizePath(p.folderPath));

    // Ordena os caminhos para garantir que as raízes venham antes das subpastas
    folderPaths.sort();

    const highestFolders = new Set<string>();

    folderPaths.forEach((folder) => {
      // Se já houver uma pasta ancestral na lista, ignora essa subpasta
      if (
        !Array.from(highestFolders).some((parent) =>
          folder.startsWith(parent + "/")
        )
      ) {
        highestFolders.add(folder);
      }
    });

    return Array.from(highestFolders);
  };

  useEffect(() => {
    if (open) {
      setLoading(true);
      setShouldShowContent(false);
      setError("");

      getAllUsersAndFoldersPermissions(currentUser)
        .then((data) => {
          const usersWithPermissions = data.filter(
            (item) => item.permissions && item.permissions.length > 0
          );

          const extractEntity = (folderPath: string) => {
            const normalizePath = (path: string) =>
              path.replace(/\\/g, "/").replace(/\/+/g, "/");

            const normalizedPath = normalizePath(folderPath).toLowerCase();
            const rawRoot = window._env_?.REACT_APP_ROOT_FOLDER || "";
            const normalizedRoot =
              normalizePath(rawRoot).replace(/\/+$/, "").toLowerCase() + "/";

            if (!normalizedPath.startsWith(normalizedRoot)) {
              return folderPath;
            }

            const relative = normalizedPath.slice(normalizedRoot.length);
            const firstPart = relative.split("/")[0];
            return firstPart && firstPart !== "" ? firstPart : null;
          };

          const filteredPermissions = usersWithPermissions.flatMap((user) => {
            const highestFolders = getHighestLevelFolders(user.permissions);

            return highestFolders.map((folderPath) => ({
              id: `${user.userId}-${folderPath}`,
              entidade: extractEntity(folderPath),
              login: user.login,
              nome: user.fullName,
              pasta: folderPath,
            }));
          });

          setPermissions(filteredPermissions);
          setLoading(false);
          setShouldShowContent(true);
        })
        .catch((err) => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [open]);

  // Filtrar permissões com base nos filtros aplicados
  const filteredPermissions = permissions
    .filter((perm) => perm.entidade !== null) // <-- evita problemas no sort e includes
    .filter((perm) => {
      const matchesType =
        filterType === "login"
          ? perm.login.toLowerCase().includes(filterValue.toLowerCase())
          : perm.nome.toLowerCase().includes(filterValue.toLowerCase());

      const matchesEntidade =
        filterEntidades.length === 0 ||
        filterEntidades.some((ent) =>
          perm.entidade!.toLowerCase().includes(ent.toLowerCase())
        );

      return matchesType && matchesEntidade;
    })
    .sort((a, b) => a.entidade!.localeCompare(b.entidade!)); // <-- usa ! pois já filtrou os null
  // Ordena após a filtragem, se necessário

  const handleDownloadCSV = () => {
    // Definir os cabeçalhos do CSV
    const headers = ["Entidade", "Login", "Nome", "Pasta"];

    // Mapear os dados filtrados para o formato adequado
    const csvRows = filteredPermissions.map((perm) => [
      `"${perm.entidade}"`, // Coloca a entidade entre aspas duplas
      `"${perm.login}"`, // Coloca o login entre aspas duplas
      `"${perm.nome}"`, // Coloca o nome entre aspas duplas
      `"${perm.pasta}"`, // Coloca a pasta entre aspas duplas
    ]);

    // Construir o conteúdo do CSV
    let csvContent = headers.join(",") + "\n";
    csvRows.forEach((rowArray) => {
      const row = rowArray.join(",");
      csvContent += row + "\n";
    });

    // Criar um Blob a partir do conteúdo CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    // Criar um link temporário para download
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "permissoes_entidades.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportXLSX = () => {
    const exportSource =
      selectedIds.length > 0
        ? permissions.filter((perm) => selectedIds.includes(perm.id))
        : filteredPermissions;

    if (exportSource.length === 0) {
      toast.info("Nenhum dado disponível para exportar.");
      return;
    }

    // Agrupar por login (cada usuário pode ter múltiplas pastas)
    const grouped = exportSource.reduce(
      (acc, perm) => {
        if (!acc[perm.login]) {
          acc[perm.login] = {
            login: perm.login,
            folderPaths: [],
            accessPermissions: [],
          };
        }
        acc[perm.login].folderPaths.push(perm.pasta);
        acc[perm.login].accessPermissions.push("true"); // ou "false" se precisar marcar removidos
        return acc;
      },
      {} as Record<
        string,
        { login: string; folderPaths: string[]; accessPermissions: string[] }
      >
    );

    // Monta os dados no formato do import
    const exportData = Object.values(grouped).map((user) => ({
      Login: user.login,
      FolderPaths: user.folderPaths.join(","),
      AccessPermissions: user.accessPermissions.join(","),
    }));

    // Cria a planilha
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Usuarios");

    // Gera o nome com data automática
    const now = new Date();
    const timestamp = now
      .toISOString()
      .replace("T", "_")
      .replace(/:/g, "-")
      .split(".")[0];
    const fileName = `usuarios_export_${timestamp}.xlsx`;

    XLSX.writeFile(workbook, fileName);

    toast.success(`Arquivo "${fileName}" exportado com sucesso!`);
  };

  const handleFilterChange = () => {
    setIsFiltered(true);
    setSelectedIds([]); // Limpa a seleção de itens quando o filtro for aplicado
  };

  useEffect(() => {
    if (filterValue || filterEntidades.length > 0) {
      setIsFiltered(true);
      setSelectedIds([]);
    } else {
      setIsFiltered(false);
      setSelectedIds([]);
    }
  }, [filterValue, filterEntidades]);

  // Normaliza o caminho para lidar com diferentes tipos de barra (\\ ou /)
  const normalizePath = (path: string) =>
    path.replace(/\\/g, "/").replace(/\/+/g, "/");

  // Extraímos a "entidade" da pasta, que é a pasta diretamente após o rootFolder
  const extractEntityAndFolderPath = (folderPath: string) => {
    const normalizedPath = normalizePath(folderPath);

    // A raiz do diretório que é configurada na variável ambiente
    const normalizedRootFolder = normalizePath(
      window._env_?.REACT_APP_ROOT_FOLDER || ""
    ).endsWith("/")
      ? normalizePath(window._env_?.REACT_APP_ROOT_FOLDER || "")
      : normalizePath(window._env_?.REACT_APP_ROOT_FOLDER || "") + "/";

    // Verifica se o caminho começa com o rootFolder
    if (!normalizedPath.startsWith(normalizedRootFolder))
      return { entity: null, fullPath: null };

    // Remove o rootFolder e pega o restante do caminho
    const relativePath = normalizedPath.slice(normalizedRootFolder.length);

    // Divide o restante do caminho em partes
    const pathParts = relativePath.split("/");

    // A primeira parte do caminho após o rootFolder é a entidade
    const entity = pathParts[0] || null;
    const fullPath = pathParts.join("/");

    return { entity, fullPath }; // Retorna a entidade e o caminho completo
  };

  // Depende da abertura do modal

  const handleSelectAll = () => {
    const filteredIds = filteredPermissions.map((perm) => perm.id);
    setSelectedIds((prev) =>
      prev.length === filteredIds.length ? [] : filteredIds
    );
  };

  const handleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleRemoveFolders = () => {
    if (selectedIds.length === 0) {
      toast.error("Nenhuma pasta selecionada para remoção.");
      return;
    }

    const foldersToDelete = selectedIds
      .map((id) => permissions.find((perm) => perm.id === id))
      .filter((perm): perm is FolderPermission => perm !== undefined);

    setFoldersToRemove(foldersToDelete);

    // Agrupar pastas por usuário
    const usersGrouped = foldersToDelete.reduce(
      (acc, folder) => {
        if (!acc[folder.login]) {
          acc[folder.login] = {
            userId: folder.id,
            login: folder.login,
            name: folder.nome,
            entidades: [],
          };
        }
        if (folder.entidade !== null) {
          acc[folder.login].entidades.push(folder.entidade);
        }
        return acc;
      },
      {} as Record<
        string,
        { userId: string; login: string; name: string; entidades: string[] }
      >
    );

    setUsersToDelete(Object.values(usersGrouped)); // Atualiza o estado com os usuários
    setIsRemoveConfirmationOpen(true);
  };

  const handleCancelRemove = () => {
    setIsRemoveConfirmationOpen(false);
    setFoldersToRemove([]);
  };

  const handleConfirmRemove = async () => {
    try {
      setLoading(true);

      // Chamar a API para remover as permissões de pasta dos usuários
      await removePermissionsForUsers(
        currentUser,
        foldersToRemove.map((folder) => ({
          TargetUsername: folder.login,
          FolderPaths: [folder.pasta],
        }))
      );

      // Atualiza o estado das permissões
      setPermissions((prev) =>
        prev.filter(
          (perm) => !foldersToRemove.some((folder) => folder.id === perm.id)
        )
      );

      toast.success("Pastas removidas com sucesso!");
    } catch (error) {
      toast.error("Erro ao remover pastas.");
    } finally {
      setIsRemoveConfirmationOpen(false);
      setFoldersToRemove([]);
      setLoading(false);
    }
  };

  // Handler para remover permissões selecionadas (simulação)
  const handleRemovePermissions = async () => {
    if (selectedIds.length === 0) {
      toast.error("Nenhuma permissão selecionada para remoção.");
      return;
    }

    // Agrupa as permissões por usuário
    const groupedPermissions = selectedIds.reduce(
      (acc, id) => {
        const permission = permissions.find((perm) => perm.id === id);
        if (permission) {
          if (!acc[permission.login]) {
            acc[permission.login] = [];
          }
          acc[permission.login].push(permission.pasta);
        }
        return acc;
      },
      {} as Record<string, string[]>
    );

    // Prepara a estrutura para o backend
    const usersPermissions = Object.entries(groupedPermissions).map(
      ([login, folderPaths]) => ({
        TargetUsername: login,
        FolderPaths: folderPaths,
      })
    );

    try {
      setLoading(true);

      // Chama o método do `api.tsx`
      await removePermissionsForUsers(currentUser, usersPermissions);

      // Atualiza as permissões locais, removendo as selecionadas
      setPermissions((prev) =>
        prev.filter((perm) => !selectedIds.includes(perm.id))
      );

      setSelectedIds([]);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      toast.error(error.message || "Erro ao remover permissões.");
    }
  };

  const allSelected =
    filteredPermissions.length > 0 &&
    selectedIds.length === filteredPermissions.length;

  const entidadeList = Array.from(
    new Set(
      permissions
        .map((perm) => {
          const normalizePath = (path: string) =>
            path.replace(/\\/g, "/").replace(/\/+/g, "/");

          const root =
            normalizePath(window._env_?.REACT_APP_ROOT_FOLDER || "")
              .replace(/\/+$/, "")
              .toLowerCase() + "/";

          const fullPath = normalizePath(perm.pasta).toLowerCase();

          const startsWithRoot = fullPath.startsWith(root);

          if (!startsWithRoot) return null;

          const relative = fullPath.slice(root.length);
          const entidade = relative.split("/")[0];

          return entidade;
        })
        .filter((x): x is string => x !== null)
    )
  ).sort((a, b) => {
    const isNumA = /^\d/.test(a);
    const isNumB = /^\d/.test(b);

    // 🔹 Números vêm antes de letras
    if (isNumA && !isNumB) return -1;
    if (!isNumA && isNumB) return 1;

    // 🔹 Se ambos são do mesmo tipo, ordena alfabeticamente
    return a.localeCompare(b, "pt-BR", { sensitivity: "base", numeric: true });
  });

  return (
    <>
      {shouldShowContent && (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="lg"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              maxHeight: "580px", // Altura máxima do modal
              minHeight: "580px", // Altura mínima fixa para o modal
              minWidth: "1200px", // Largura mínima
              overflow: "hidden", // Remove scroll externo
            },
          }}
        >
          <DialogTitle>
            Gerenciamento de Entidades x Usuários
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              padding: 3,
              display: "flex",
              flexDirection: "column",
              height: "calc(100% - 64px)", // Garante que o conteúdo ocupe o espaço restante do modal
            }}
          >
            {/* Box de Filtros */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                marginBottom: 2,
                flexWrap: "wrap",
              }}
            >
              {/* Filtro Estático: Filtrar por Entidade */}
              {/* Filtro Estático: Filtrar por Entidade */}
              <Box
                sx={{
                  padding: 2,
                  marginTop: 2,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#333333" : "#e3f2fd", // 🔹 mantém fundo azul
                  borderRadius: 1,
                  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.8)",
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Autocomplete
                  multiple
                  options={entidadeList}
                  value={filterEntidades}
                  onChange={(event, newValue) => setFilterEntidades(newValue)}
                  renderTags={(value: string[], getTagProps) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        maxHeight: "70px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingRight: "4px",
                      }}
                    >
                      {value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          size="small"
                          sx={{
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#555"
                                : "#e3f2fd",
                            fontSize: "0.8rem",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filtrar por entidade"
                      placeholder="Digite para buscar..."
                      size="small"
                      variant="outlined"
                      sx={{
                        width: "250px",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "#444444" : "#ffffff", // 🔹 input branco
                      }}
                    />
                  )}
                  sx={{
                    minWidth: 250,
                    backgroundColor: "transparent", // 🔹 remove cinza atrás do campo
                    boxShadow: "none",
                    borderRadius: 1,
                    "& .MuiAutocomplete-tag": {
                      maxWidth: "100px",
                    },
                  }}
                />
              </Box>

              {/* Filtro Dinâmico: Selecionar entre "Login" ou "Nome" */}
              <Box
                sx={{
                  padding: 2,
                  marginTop: 2,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#333333" : "#e3f2fd",
                  borderRadius: 1,
                  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.8)",
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <TextField
                  select
                  label="Filtrar por"
                  value={filterType}
                  onChange={(e) =>
                    setFilterType(e.target.value as "login" | "nome")
                  }
                  size="small"
                  variant="outlined"
                  sx={{
                    width: "150px", // Mesmo tamanho do campo "Filtrar por" no ManagerUserModal
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#444444" : "#ffffff",
                  }}
                  SelectProps={{
                    native: true,
                    sx: {
                      fontSize: "0.875rem",
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      fontSize: "0.875rem",
                    },
                  }}
                  InputProps={{
                    sx: {
                      height: "36px",
                      fontSize: "0.875rem",
                      padding: "0 14px",
                      border: "none",
                    },
                  }}
                >
                  <option value="login">Login</option>
                  <option value="nome">Nome</option>
                </TextField>

                <TextField
                  placeholder={`Buscar ${filterType === "login" ? "Login" : "Nome"}`}
                  size="small"
                  variant="outlined"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  sx={{
                    width: "300px", // Mesmo tamanho do campo "Buscar" no ManagerUserModal
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#444444" : "#ffffff",
                    borderRadius: "4px",
                  }}
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      fontSize: "0.875rem",
                      color: (theme) => theme.palette.text.primary,
                    },
                  }}
                  InputProps={{
                    sx: {
                      height: "36px",
                      fontSize: "0.875rem",
                      border: "none",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Tabela de Permissões */}
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.6)",
                borderRadius: 2,
                maxHeight: "400px", // Define a altura máxima para ativar o scroll
                overflow: "auto", // Ativa o scroll se o conteúdo exceder a altura
              }}
            >
              <Table stickyHeader>
                {/* Cabeçalho da Tabela */}
                <TableHead>
                  <TableRow
                    sx={{
                      height: "32px", // Altura reduzida do cabeçalho
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "#333333" : "#e3f2fd", // Garante fundo consistente
                    }}
                  >
                    {/* Cabeçalhos das colunas */}
                    <TableCell
                      sx={{
                        padding: "4px 8px",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        minWidth: "150px", // Define uma largura mínima
                      }}
                    >
                      Entidade
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "4px 8px",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        minWidth: "150px", // Define uma largura mínima
                      }}
                    >
                      Login
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "4px 8px",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        minWidth: "200px", // Define uma largura mínima
                      }}
                    >
                      Nome
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "4px 8px",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        minWidth: "300px", // Define uma largura mínima
                      }}
                    >
                      Pasta
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        padding: "4px 8px",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        minWidth: "150px",
                        position: "sticky",
                        top: 0,
                      }}
                    >
                      Selecionar
                      <Tooltip
                        title={
                          filteredPermissions.length === 0
                            ? "Nenhuma permissão disponível para seleção."
                            : allSelected
                              ? "Cancelar seleção de todas as permissões."
                              : "Selecionar todas as permissões."
                        }
                        arrow
                        placement="top"
                      >
                        <Checkbox
                          checked={allSelected}
                          onChange={handleSelectAll}
                          color="primary"
                          size="small"
                          sx={{
                            cursor:
                              filteredPermissions.length === 0
                                ? "not-allowed"
                                : "pointer",
                            color:
                              filteredPermissions.length === 0
                                ? "gray"
                                : "primary.main",
                            "&:hover": {
                              color:
                                filteredPermissions.length > 0
                                  ? "primary.dark"
                                  : "gray",
                            },
                          }}
                          disabled={filteredPermissions.length === 0}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* Corpo da Tabela */}
                <TableBody>
                  {filteredPermissions.length === 0 ? (
                    <TableRow
                      sx={{
                        height: "28px", // Altura reduzida para linhas vazias
                      }}
                    >
                      <TableCell
                        colSpan={5} // Reflete o número total de colunas (4 + 1 checkbox)
                        align="center"
                        sx={{
                          fontSize: "0.875rem",
                          padding: "6px", // Menos espaço interno
                        }}
                      >
                        Nenhuma permissão encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPermissions.map((perm) => {
                      const isSelected = selectedIds.includes(perm.id);

                      return (
                        <TableRow
                          key={perm.id}
                          sx={{
                            height: "28px", // Altura reduzida da linha
                            backgroundColor: isSelected
                              ? "rgba(244, 67, 54, 0.2)" // Fundo vermelho claro para linha selecionada
                              : "transparent", // Fundo transparente para linhas não selecionadas
                            "&:hover": {
                              backgroundColor: isSelected
                                ? "rgba(244, 67, 54, 0.3)" // Fundo vermelho mais intenso no hover
                                : theme.palette.mode === "dark"
                                  ? "#424242"
                                  : "#f1f1f1", // Fundo padrão para hover em linhas não selecionadas
                            },
                            transition: "background-color 0.3s ease-in-out", // Transição suave ao selecionar
                          }}
                        >
                          {/* Dados da Permissão */}
                          <TableCell
                            sx={{
                              padding: "2px 6px",
                              fontSize: "0.75rem",
                              lineHeight: "1",
                            }}
                          >
                            {perm.entidade}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "2px 6px",
                              fontSize: "0.75rem",
                              lineHeight: "1",
                            }}
                          >
                            {perm.login}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "2px 6px",
                              fontSize: "0.75rem",
                              lineHeight: "1",
                            }}
                          >
                            {perm.nome}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "2px 6px",
                              fontSize: "0.75rem",
                              lineHeight: "1",
                            }}
                          >
                            {perm.pasta}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              padding: "2px 6px",
                              fontSize: "0.75rem",
                              lineHeight: "1",
                            }}
                          >
                            <Tooltip
                              title="Selecionar para remover permissão"
                              arrow
                              placement="right"
                            >
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleSelect(perm.id)}
                                color="error"
                                size="small"
                                sx={{
                                  backgroundColor: "transparent", // Remove fundo arredondado
                                  boxShadow: "none", // Remove qualquer sombra
                                  padding: "0", // Remove padding extra
                                  "&:hover": {
                                    backgroundColor: "transparent", // Mantém o fundo transparente no hover
                                  },
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <Divider />
          <DialogActions
            sx={{
              padding: "1px 2px",
              minHeight: "40px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {/* Texto com contador de seleção */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                textAlign: "left",
                paddingLeft: "8px",
                fontSize: "0.875rem",
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selectedIds.length > 0 ? (
                <Typography sx={{ fontSize: "0.775rem" }}>
                  {selectedIds.length === filteredPermissions.length
                    ? `Todas as ${selectedIds.length} permissões estão selecionadas.`
                    : selectedIds.length === 1
                      ? "1 permissão selecionada."
                      : `${selectedIds.length} permissões selecionadas.`}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: "0.775rem", color: "gray" }}>
                  Nenhuma permissão selecionada.
                </Typography>
              )}
            </Box>

            {/* Botões de ação */}
            <Button
              onClick={onClose}
              color="primary"
              size="small"
              sx={{
                color: "primary.main",
                backgroundColor: "transparent",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "transparent",
                },
              }}
            >
              Fechar
            </Button>

            <Button
              onClick={handleDownloadCSV}
              color="primary"
              size="small"
              sx={{
                color: "primary.main",
                backgroundColor: "transparent",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "transparent",
                },
              }}
            >
              Baixar CSV
            </Button>

            {/* --- BOTÕES FIXOS: REMOVER E EXPORTAR --- */}
            <Button
              onClick={handleRemoveFolders}
              color="error"
              size="small"
              disabled={selectedIds.length === 0}
              sx={{
                backgroundColor: "transparent",
                boxShadow: "none",
                color: selectedIds.length === 0 ? "gray" : "error.main",
                "&:hover": {
                  backgroundColor:
                    selectedIds.length === 0
                      ? "transparent"
                      : "rgba(255,0,0,0.1)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "transparent",
                },
              }}
            >
              Remover
            </Button>

            <Button
              onClick={handleExportXLSX}
              color="primary"
              size="small"
              disabled={selectedIds.length === 0}
              sx={{
                backgroundColor: "transparent",
                boxShadow: "none",
                color: selectedIds.length === 0 ? "gray" : "primary.main",
                "&:hover": {
                  backgroundColor:
                    selectedIds.length === 0
                      ? "transparent"
                      : "rgba(0, 0, 0, 0.04)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "transparent",
                },
              }}
            >
              Exportar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog open={isRemoveConfirmationOpen} onClose={handleCancelRemove}>
        <DialogTitle>Remoção de acesso às pastas</DialogTitle>
        <DialogContent>
          <Typography>
            O acesso dos usuário às seguintes pastas serão removidos:
            <br />
            {usersToDelete.length > 0 &&
              usersToDelete.map((user) => (
                <Box key={user.userId} sx={{ marginTop: 2 }}>
                  <Typography variant="body1">
                    <strong>
                      {user.login} - {user.name}
                    </strong>
                  </Typography>
                  <ul>
                    {user.entidades.map((entidade, idx) => {
                      // Aqui, ao invés de usar "entidade", usamos "pasta" que é o caminho completo
                      const folderPath = permissions.find(
                        (perm) =>
                          perm.login === user.login &&
                          perm.entidade !== null &&
                          perm.entidade === entidade
                      )?.pasta; // Encontra o caminho completo da pasta

                      return folderPath ? (
                        <li key={idx}>
                          <Typography variant="body2">
                            {folderPath}{" "}
                            {/* Exibe o caminho completo da pasta */}
                          </Typography>
                        </li>
                      ) : null;
                    })}
                  </ul>
                </Box>
              ))}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCancelRemove}
            color="primary"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmRemove}
            color="primary"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {loading ? "Removendo..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManagerEntitiesModal;
